/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import { EuiListGroup, EuiPanel, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useConfig } from '@modules/cui/ConfigContext'
import Feature from '@modules/utils/feature'

import { getPricingLink } from './utils'

const PricingInfoPanel = () => {
  const serverlessFeatureAvailable = useConfig(Feature.serverless)

  const rateLinks = [
    {
      label: (
        <FormattedMessage
          id='pricing-info-panel.rates.cloud-hosted.link'
          defaultMessage='Cloud hosted'
        />
      ),
      href: getPricingLink('stack_hosted'),
      target: '_blank',
      'data-test-id': 'pricing-info-panel.rates.cloud-hosted.link',
    },
    ...(serverlessFeatureAvailable
      ? [
          {
            label: (
              <FormattedMessage
                id='pricing-info-panel.rates.serverless-es.link'
                defaultMessage='Serverless Elasticsearch'
              />
            ),
            href: getPricingLink('elasticsearch'),
            target: '_blank',
            'data-test-id': 'pricing-info-panel.rates.serverless-es.link',
          },
          {
            label: (
              <FormattedMessage
                id='pricing-info-panel.rates.serverless-observability.link'
                defaultMessage='Serverless Observability'
              />
            ),
            href: getPricingLink('observability'),
            target: '_blank',
            'data-test-id': 'pricing-info-panel.rates.serverless-observability.link',
          },
          {
            label: (
              <FormattedMessage
                id='pricing-info-panel.rates.serverless-security.link'
                defaultMessage='Serverless Security'
              />
            ),
            href: getPricingLink('security'),
            target: '_blank',
            'data-test-id': 'pricing-info-panel.rates.serverless-security.link',
          },
        ]
      : []),
  ]

  const dimensionLinks = [
    {
      label: (
        <FormattedMessage
          id='pricing-info-panel.dimensions.cloud-hosted.link'
          defaultMessage='Cloud hosted deployments'
        />
      ),
      href: 'https://ela.st/cloud-hosted-billing-dimensions',
      target: '_blank',
      'data-test-id': 'pricing-info-panel.dimensions.cloud-hosted.link',
    },
    ...(serverlessFeatureAvailable
      ? [
          {
            label: (
              <FormattedMessage
                id='pricing-info-panel.dimensions.serverless.link'
                defaultMessage='Serverless projects'
              />
            ),
            href: 'https://ela.st/serverless-billing-dimensions',
            target: '_blank',
            'data-test-id': 'pricing-info-panel.dimensions.serverless.link',
          },
        ]
      : []),
  ]

  const costToolsLinks = [
    {
      label: (
        <FormattedMessage
          id='pricing-info-panel.costs-tools.cloud-hosted.link'
          defaultMessage='Cloud hosted'
        />
      ),
      href: 'https://ela.st/cloud-hosted-calculator',
      target: '_blank',
      'data-test-id': 'pricing-info-panel.costs-tools.cloud-hosted.link',
    },
    ...(serverlessFeatureAvailable
      ? [
          {
            label: (
              <FormattedMessage
                id='pricing-info-panel.costs-tools.serverless.link'
                defaultMessage='Serverless projects'
              />
            ),
            href: 'https://ela.st/pricing-serverless-estimator',
            target: '_blank',
            'data-test-id': 'pricing-info-panel.costs-tools.serverless.link',
          },
        ]
      : []),
  ]

  return (
    <EuiPanel hasShadow={false} hasBorder={true} data-test-id='pricing-info-panel' grow={false}>
      <EuiTitle size='xs'>
        <h3>
          <FormattedMessage id='pricing-info-panel.title' defaultMessage='Pricing information' />
        </h3>
      </EuiTitle>

      <EuiSpacer size='m' />

      <EuiText size='s'>
        <h4>
          <FormattedMessage id='pricing-info-panel.cost-tools.title' defaultMessage='Cost tools' />
        </h4>
      </EuiText>

      <EuiSpacer size='s' />

      <EuiListGroup
        flush={true}
        listItems={costToolsLinks}
        color='primary'
        size='s'
        gutterSize='none'
      />

      <EuiSpacer size='s' />

      <EuiText size='s'>
        <h4>
          <FormattedMessage id='pricing-info-panel.rates.title' defaultMessage='Rates' />
        </h4>
      </EuiText>

      <EuiSpacer size='s' />

      <EuiListGroup flush={true} listItems={rateLinks} color='primary' size='s' gutterSize='none' />

      <EuiSpacer size='s' />

      <EuiText size='s'>
        <h4>
          <FormattedMessage
            id='pricing-info-panel.dimensions.title'
            defaultMessage='Billing dimensions'
          />
        </h4>
      </EuiText>

      <EuiSpacer size='s' />

      <EuiListGroup
        flush={true}
        listItems={dimensionLinks}
        color='primary'
        size='s'
        gutterSize='none'
      />
    </EuiPanel>
  )
}

export default PricingInfoPanel
